import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AuthQuery } from '@app/auth/auth.query';
import { Kontrolltermin } from '../../../services/kontrolltermin.service';
import { ChangeTechnikerDialogComponent } from '../../dialogs/change-techniker-dialog/change-techniker-dialog.component';

@Component({
  selector: 'app-header-info',
  templateUrl: './header-info.component.html',
  styleUrls: ['./header-info.component.scss']
})
export class HeaderInfoComponent implements OnInit {
  @Input() kontrolltermin!: Kontrolltermin;
  @Input() editableFields: string[] = [];
  @Input() showTimeFields: boolean = true;
  @Input() sourceStatus: string = '';
  @Output() fieldChanged = new EventEmitter<{ field: string; value: any }>();
  @Output() batchFieldsChanged = new EventEmitter<{ [key: string]: any }>();

  constructor(
    private dialog: MatDialog,
    private authQuery: AuthQuery
  ) { }

  ngOnInit(): void {
  }

  getStatusBorderColor(): string {
    const colorMap: { [key: string]: string } = {
      'geplant': '#6c757d',
      'aktuell': '#5a7d9a',
      'bearbeitung': '#f1c40f',
      'freigabe': '#e67e22',
      'abgeschlossen': '#4caf50',
      'geloescht': '#dc3545'
    };
    return colorMap[this.sourceStatus] || '#6c757d';
  }

  getStatusBadgeLabel(): string {
    const labelMap: { [key: string]: string } = {
      'geplant': 'Geplant',
      'aktuell': 'Aktuell',
      'bearbeitung': 'In Bearbeitung',
      'freigabe': 'Freigabe',
      'abgeschlossen': 'Abgeschlossen',
      'geloescht': 'Gelöscht'
    };
    return labelMap[this.sourceStatus] || this.getStatusText();
  }

  isFieldEditable(field: string): boolean {
    return this.editableFields.includes(field);
  }

  getStatusText(): string {
    if (!this.kontrolltermin) return '-';
    
    // First try to use status object if available
    if (this.kontrolltermin.status?.beschreibung) {
      return this.kontrolltermin.status.beschreibung;
    }
    
    // Fallback to status_id lookup
    if (this.kontrolltermin.status_id !== null && this.kontrolltermin.status_id !== undefined) {
      const statusMap: {[key: number]: string} = {
        1: 'Gelöscht',
        2: 'Abgebrochen',
        3: 'Fehlerhaft',
        4: 'Geplant',
        5: 'Aktuell',
        6: 'In Bearbeitung',
        7: 'Freigabe',
        8: 'Abgeschlossen'
      };
      return statusMap[this.kontrolltermin.status_id] || 'Unbekannt';
    }
    
    return '-';
  }

  getMonitoringMonth(): string {
    if (!this.kontrolltermin) return '-';
    
    // First try monat_plan if available
    if (this.kontrolltermin.monat_plan) {
      return this.kontrolltermin.monat_plan;
    }
    
    // Fallback to extracting month from datum_durchfuerung
    if (this.kontrolltermin.datum_durchfuerung) {
      const date = new Date(this.kontrolltermin.datum_durchfuerung);
      const monthNames = [
        'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
        'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
      ];
      return monthNames[date.getMonth()] || '-';
    }
    
    return '-';
  }

  getTechnikerName(): string {
    if (!this.kontrolltermin?.techniker) return '-';
    
    const techniker: any = this.kontrolltermin.techniker;
    
    if (techniker.full_name) return techniker.full_name;
    if (techniker.display_name) return techniker.display_name;
    if (techniker.name) return techniker.name;
    
    // Fallback to vorname + nachname if available
    const vorname = techniker.vorname || techniker.first_name || '';
    const nachname = techniker.nachname || techniker.last_name || '';
    const name = (vorname + ' ' + nachname).trim();
    
    return name || '-';
  }

  /**
   * Check if Prüfdatum should be shown
   * Only show when status is in-bearbeitung (6), freigabe (7), or abgeschlossen (8)
   */
  shouldShowPruefdatum(): boolean {
    if (!this.kontrolltermin?.status_id) return false;
    return [6, 7, 8].includes(this.kontrolltermin.status_id);
  }

  /**
   * Format Durchführungszeit (ist_zeit) in hours and minutes if > 60 minutes
   */
  formatDurchfuehrungszeit(): string {
    if (!this.kontrolltermin?.ist_zeit) return '-';
    
    const minutes = Number(this.kontrolltermin.ist_zeit);
    if (isNaN(minutes) || minutes <= 0) return '-';
    
    if (minutes <= 60) {
      return `${minutes} Min`;
    }
    
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    
    if (remainingMinutes === 0) {
      return `${hours}h`;
    }
    
    return `${hours}h ${remainingMinutes} Min`;
  }

  /**
   * Check if user can edit kontrolltermin (super user, compadmin, or büro)
   */
  canEditKontrolltermin(): boolean {
    const role = this.authQuery.role;
    return role === 1 || role === 2 || role === 11; // 1 = SuperUser, 2 = CompAdmin, 11 = Büro
  }

  /**
   * Open dialog to edit kontrolltermin (techniker, prüfdatum, times)
   */
  openEditDialog(): void {
    console.log('HeaderInfoComponent: openChangeTechnikerDialog() called');
    console.log('HeaderInfoComponent: kontrolltermin:', this.kontrolltermin);
    console.log('HeaderInfoComponent: currentTechnikerId:', this.kontrolltermin.techniker_id);
    
    const dialogRef = this.dialog.open(ChangeTechnikerDialogComponent, {
      width: '500px',
      data: {
        kontrolltermin: this.kontrolltermin,
        currentTechnikerId: this.kontrolltermin.techniker_id
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log('HeaderInfoComponent: Dialog closed with result:', result);
      
      if (result && result.event === 'save') {
        // Collect all changed fields into one batch
        const changes: { [key: string]: any } = {};
        
        if (result.techniker_id !== undefined) {
          changes['techniker_id'] = result.techniker_id;
        }
        if (result.datum_durchfuerung !== undefined) {
          changes['datum_durchfuerung'] = result.datum_durchfuerung;
        }
        if (result.soll_zeit !== undefined) {
          changes['soll_zeit'] = result.soll_zeit;
        }
        if (result.ist_zeit !== undefined) {
          changes['ist_zeit'] = result.ist_zeit;
        }

        console.log('HeaderInfoComponent: Emitting batch changes:', changes);
        this.batchFieldsChanged.emit(changes);
      }
    });
  }

}